// Push Notifications Utility for The Calm Keeper Compass

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Convert base64 URL to Uint8Array (required for applicationServerKey)
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

// Check if push notifications are supported
export function isPushSupported() {
  return 'serviceWorker' in navigator && 
         'PushManager' in window && 
         'Notification' in window;
}

// Get current permission status
export function getPermissionStatus() {
  if (!isPushSupported()) return 'unsupported';
  return Notification.permission; // 'granted', 'denied', or 'default'
}

// Request notification permission
export async function requestPermission() {
  if (!isPushSupported()) {
    throw new Error('Push notifications are not supported in this browser');
  }
  
  const permission = await Notification.requestPermission();
  return permission;
}

// Get VAPID public key from server
async function getVapidPublicKey() {
  const response = await fetch(`${API}/push/vapid-key`);
  if (!response.ok) {
    throw new Error('Failed to get VAPID public key');
  }
  const data = await response.json();
  return data.publicKey;
}

// Subscribe to push notifications
export async function subscribeToPush(userId = null) {
  if (!isPushSupported()) {
    throw new Error('Push notifications are not supported');
  }
  
  // Check permission
  const permission = await requestPermission();
  if (permission !== 'granted') {
    throw new Error('Notification permission denied');
  }
  
  try {
    // Get service worker registration
    const registration = await navigator.serviceWorker.ready;
    
    // Get VAPID public key
    const vapidPublicKey = await getVapidPublicKey();
    const applicationServerKey = urlBase64ToUint8Array(vapidPublicKey);
    
    // Subscribe to push manager
    const subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: applicationServerKey
    });
    
    // Send subscription to backend
    const response = await fetch(`${API}/push/subscribe`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        subscription: subscription.toJSON(),
        user_id: userId
      })
    });
    
    if (!response.ok) {
      throw new Error('Failed to save subscription to server');
    }
    
    // Save subscription status to localStorage
    localStorage.setItem('calm_keeper_push_enabled', 'true');
    
    return subscription;
    
  } catch (error) {
    console.error('Error subscribing to push:', error);
    throw error;
  }
}

// Unsubscribe from push notifications
export async function unsubscribeFromPush() {
  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    
    if (subscription) {
      // Unsubscribe locally
      await subscription.unsubscribe();
      
      // Notify backend
      await fetch(`${API}/push/unsubscribe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          subscription: subscription.toJSON()
        })
      });
    }
    
    // Update localStorage
    localStorage.setItem('calm_keeper_push_enabled', 'false');
    
    return true;
    
  } catch (error) {
    console.error('Error unsubscribing from push:', error);
    throw error;
  }
}

// Check if currently subscribed
export async function isSubscribed() {
  if (!isPushSupported()) return false;
  
  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    return !!subscription;
  } catch (error) {
    console.error('Error checking subscription:', error);
    return false;
  }
}

// Get subscription status from localStorage (quick check)
export function isPushEnabled() {
  return localStorage.getItem('calm_keeper_push_enabled') === 'true';
}

// Schedule a gentle reminder (triggered from settings)
export const REMINDER_PRESETS = [
  {
    id: 'morning',
    label: 'Morning Check-in',
    time: '09:00',
    message: 'Good morning! Time to check your day mode and set your intentions 🌅'
  },
  {
    id: 'midday',
    label: 'Midday Pause',
    time: '13:00',
    message: 'Take a breath. How is your day going? Remember: you are doing enough 🌿'
  },
  {
    id: 'evening',
    label: 'Evening Review',
    time: '19:00',
    message: 'Time for your gentle review. Reflect on what you actually did today 🌙'
  },
  {
    id: 'brain_dump',
    label: 'Brain Dump Reminder',
    time: '11:00',
    message: 'Feeling overwhelmed? Take a moment to offload your thoughts 💭'
  }
];

// Save reminder settings to localStorage
export function saveReminderSettings(settings) {
  localStorage.setItem('calm_keeper_reminder_settings', JSON.stringify(settings));
}

// Get reminder settings from localStorage
export function getReminderSettings() {
  const stored = localStorage.getItem('calm_keeper_reminder_settings');
  return stored ? JSON.parse(stored) : {
    enabled: false,
    reminders: {}
  };
}
